import { DataRecord } from '@components/DataRecord/DataRecord.js';
import { debounceTime, distinctUntilChanged, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { catchError, combineLatest, EMPTY, filter, forkJoin, map, merge, of, share, Subject, take, tap, throwError, skip, ReplaySubject } from 'rxjs';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { forEachKey, getID, replaceTokens } from '@components/Utility/index.js';
import { TOKEN } from '@components/Environment.js';
import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
import { eventContextGet } from '@components/EventConfigManager/EventConfigManagerUtil.js';
import { Request } from '@components/Request/Request.js';
/**
 * Mixin to add list functionality to a component, such as Grid or InputSelect
 * @param classToExtend
 * @constructor
 */
export function ListMixin(classToExtend) {
    return class extends classToExtend {
        constructor() {
            super(...arguments);
            this._action = '';
            this._endpoint = '';
            this.optionDataStatus = 'stale';
            this.filteredOptionData$ = new ReplaySubject(1);
            this.sortedOptionData$ = new ReplaySubject(1); // sorting occurs after filtering so this inherently includes filtering if applicable
            this.groupedOptionId$ = new ReplaySubject(1);
            /** array of options that will always be in the list at the top. ex. ALL {value: 'ALL', display: 'All'} or {customerno: 'ALL', company: 'All'}*/
            this._staticOptionData = [];
            /** array of options returned by the List AJAX call */
            this._optionData = [];
            /** client-side filter conditions applied to response data before setting optionData*/
            this._responseFilterConditions = [];
            this.autoCompleteThreshold = 3;
            this.allOptionsById = {};
            this._deletedData = [];
            /** unique key column from the result set, otherwise id will be auto generated */
            this.optionDataIdKey = '';
            /** the name of the recordset in the response that will contain the optionData */
            this._responseResultName = 'result';
            this.alternateResultName = 'griddata';
            /**
             * TODO: resultSetName to component propertyName mapping.
             * ie. result: 'optionData'
             * pagingData: 'pagingData' etc
             */
            /** indicates if this control allows multi selection */
            this._multiSelect = false;
            /** Whether or not to perform a data request the first time the component loads. */
            this._dataRequestOnInit = true;
            /** Whether or not to perform a data request each time the parameterRecord loads data (ie. form refresh) even if the params didnt change*/
            this._dataRequestOnRecordLoad = false;
            /** Whether or not to automatically perform a data request each time a dynamic or static parameter changes. */
            this._dataRequestOnParameterChange = true;
            // protected _delayRequestUntilDrawn?: boolean; // for components like RC, the component itself tells us some of the parameters such as the start/end date of the console.
            /** Whether to allow the component to perform a dataRequest when not visible. */
            this._delayRequestUntilVisible = true;
            /** Whether to cache the results of the request. */
            this._cacheRequest = false;
            this.cacheStrategy = 'cache-first';
            /** Join configs for enriching offlinedata rows with fields from related records in the same store. */
            this._offlineDataJoins = [];
            this._offlineDataJoinSubscriptions = [];
            this._offlineDataJoinKeys = new Set();
            // Pagination properties
            this.currentPage = 1;
            this.itemsPerPage = 100;
            /** when this subject triggers, it cancels the active dataRequest$ */
            this.cancelRequest$ = new Subject();
            this.autoRefreshSetupCompleted = false;
            this._detailRecord = undefined;
            this._stream = true;
        }
        get action() { return this._action; }
        set action(value) { this._propertySet('action', value); }
        get endpoint() { return this._endpoint; }
        set endpoint(value) { this._propertySet('endpoint', value); }
        get staticParameters() { return this._staticParameters; }
        set staticParameters(value) { this._propertySet('staticParameters', value); }
        get dynamicParameters() { return this._dynamicParameters; }
        set dynamicParameters(value) { this._propertySet('dynamicParameters', value); }
        get baseDynamicParameters() { return this._baseDynamicParameters; }
        set baseDynamicParameters(value) { this._propertySet('baseDynamicParameters', value); }
        get allParameters() { return this._allParameters; }
        set allParameters(value) { this._propertySet('allParameters', value); }
        isDataFresh() { return this.optionDataStatus === 'fresh'; }
        isDataStale() { return this.optionDataStatus === 'stale'; }
        isDataPending() { return this.optionDataStatus === 'pending'; }
        get primaryDatasourceId() { return this._primaryDatasourceId; }
        set primaryDatasourceId(value) {
            this._propertySet('primaryDatasourceId', value);
            this.primaryDatasourcePropertiesSubscription?.unsubscribe();
            if (value && this.drawn) {
                this.setupSyncPrimaryDatasourceProperties();
            }
        }
        get primaryDatasourceComponent() {
            if (this.dependencies.primaryDatasourceComponent)
                return this.dependencies.primaryDatasourceComponent;
            if (!this._primaryDatasourceId || !this.formContext)
                return undefined;
            else
                return this.formContext.componentGet(this._primaryDatasourceId);
        }
        get primaryDatasourceComponent$() {
            if (this.dependencies.primaryDatasourceComponent) {
                return of(this.dependencies.primaryDatasourceComponent).pipe(takeUntil(this.destroy$));
            }
            if (!this._primaryDatasourceComponent$) {
                // race condition, primaryDatasourceId might get set before the component exists
                this._primaryDatasourceComponent$ = this.propertyGet$('primaryDatasourceId').pipe(takeUntil(this.destroy$), switchMap((id) => this.formContext.componentGet$(id)), shareReplay(1));
            }
            return this._primaryDatasourceComponent$;
        }
        get staticOptionData() { return this._staticOptionData; }
        set staticOptionData(value) {
            value = value.map((option, index) => {
                option = this.formatStaticOptionData(option);
                const id = this.addOptionId(option);
                if (this.optionDataType === 'Record' && !(option instanceof DataRecord)) {
                    option = this.recordizeOption(option);
                    option.index = index;
                }
                this.allOptionsById[id] = option;
                return option;
            });
            this._propertySet('staticOptionData', value);
        }
        get optionData() { return this._optionData; }
        set optionData(value) {
            if (this.primaryDatasourceComponent) {
                // when updating our optionData, we want the primary list to handle everything. So we will set its property which will in turn send it back to this replica
                // this should only trigger when doing things like adding rows to the replica component or drag and dropping in the replica etc.
                console.log('Replica Datasource updating Primary Datasource due to an add/delete/drag and drop/etc.');
                this.primaryDatasourceComponent.optionData = value;
            }
            else {
                this._propertySet('optionData', value);
            }
        }
        get responseFilterConditions() { return this._responseFilterConditions; }
        set responseFilterConditions(value) {
            const prev = this._responseFilterConditions;
            this._propertySet('responseFilterConditions', value);
            if (prev !== value && this.optionDataStatus === 'fresh') {
                this.dataRequest$().pipe(take(1)).subscribe();
            }
        }
        get metaData() { return this._metaData; }
        set metaData(value) {
            this._propertySet('metaData', value);
        }
        get autoCompleteFlag() { return this._autoCompleteFlag || false; }
        set autoCompleteFlag(value) {
            this._propertySet('autoCompleteFlag', value);
        }
        get autoCompleteValue() { return this._autoCompleteValue || ''; }
        set autoCompleteValue(value) {
            this._propertySet('autoCompleteValue', value);
        }
        get changeSubject() {
            return this.primaryDatasourceComponent?.changeSubject || this._changeSubject;
        }
        get deletedData() { return this._deletedData; }
        set deletedData(data) {
            this._propertySet('deletedData', data);
        }
        get optionDataType() { return this._optionDataType; }
        set optionDataType(type) {
            this._optionDataType = type;
            if (type === 'Record' && !this._changeSubject) {
                this._changeSubject = new Subject();
                this.subscribeToChangeSubject();
            }
            else if (type !== 'Record' && this._changeSubject) {
                this._changeSubject.complete();
                this._changeSubject = undefined;
                this.changeSubjectSubscription = undefined;
            }
        }
        subscribeToChangeSubject() { } // override in components that need to use this
        get responseResultName() { return this._responseResultName; }
        set responseResultName(val) {
            this._propertySet('responseResultName', val);
        }
        get resultSetMapping() { return this._resultSetMapping; }
        set resultSetMapping(mapping) {
            if (mapping && this._resultSetMapping) {
                forEachKey(this._resultSetMapping, (value, key) => {
                    if (mapping[key] && !mapping[key].optionData) {
                        mapping[key].optionData = value.optionData;
                        mapping[key].optionsById = value.optionsById;
                    }
                });
            }
            this._propertySet('resultSetMapping', mapping);
        }
        get multiSelect() { return this._multiSelect; }
        set multiSelect(value) {
            this._propertySet('multiSelect', value);
            if (this.drawn) {
                this.renderMultiSelect();
            }
        }
        get dataRequestOnInit() { return this._dataRequestOnInit; }
        set dataRequestOnInit(value) { this._propertySet('dataRequestOnInit', value); }
        get dataRequestOnRecordLoad() { return this._dataRequestOnRecordLoad; }
        set dataRequestOnRecordLoad(value) { this._propertySet('dataRequestOnRecordLoad', value); }
        get dataRequestOnParameterChange() { return this._dataRequestOnParameterChange; }
        set dataRequestOnParameterChange(value) { this._propertySet('dataRequestOnParameterChange', value); }
        get delayRequestUntilVisible() { return this._delayRequestUntilVisible; }
        set delayRequestUntilVisible(value) {
            this._propertySet('delayRequestUntilVisible', value);
            if (!value && !this.autoRefreshSetupCompleted) {
                this.setupAutomaticDataRequest();
            }
        }
        get cacheRequest() { return this._cacheRequest; }
        set cacheRequest(value) {
            this._propertySet('cacheRequest', value);
        }
        get offlineDataJoins() { return this._offlineDataJoins; }
        set offlineDataJoins(value) { this._propertySet('offlineDataJoins', value); }
        get parameterRecord() { return this._parameterRecord; }
        set parameterRecord(value) { this._propertySet('parameterRecord', value); }
        get baseTableName() {
            return this._baseTableName;
        }
        set baseTableName(s) {
            this._propertySet('baseTableName', s);
            if (this.primaryDatasourceComponent) {
                this.primaryDatasourceComponent.baseTableName = s;
            }
        }
        get baseTablePkName() {
            return this._baseTablePkName;
        }
        set baseTablePkName(s) {
            this._propertySet('baseTablePkName', s);
            if (this.primaryDatasourceComponent) {
                this.primaryDatasourceComponent.baseTablePkName = s;
            }
        }
        get detailRecord() { return this._detailRecord; }
        set detailRecord(record) {
            if (this._detailRecord !== record) {
                this._propertySet('detailRecord', record);
                this.setupSyncDetailRecordChanges();
            }
        }
        get currentRecord() { return this._currentRecord; }
        set currentRecord(record) {
            if (this._currentRecord !== record) {
                this._propertySet('currentRecord', record);
                this.handleCurrentRecordChanged(record);
            }
        }
        get stream() { return this._stream; }
        set stream(bool) {
            this._propertySet('stream', bool);
        }
        /**
         * Request getter which handles lazy loading the `Request` dependency. Supports injection for unit tests etc.
         */
        get Request() {
            if (!this.dependencies.Request) {
                // we didnt receive a request dependency injection, so just use the global singleton
                this.dependencies.Request = DIContainer.getInstance('Request');
            }
            return this.dependencies.Request;
        }
        init() {
            super.init();
            this._baseTableName = '';
            this._baseTablePkName = '';
            this._optionDataType = 'Object';
        }
        clearAutoCompleteOptionData() {
            this.propertySet('optionData', []);
        }
        refreshData(options = {}) {
            const obs = this.dataRequest$(options);
            obs.pipe(share()).subscribe();
            return obs;
        }
        visibilityGate$(options = {}) {
            return !this.delayRequestUntilVisible || options.ignoreVisibility
                ? of(true).pipe(switchMap(() => this.delayRefreshUntilCustom()))
                : this.isVisible$(undefined /*, this._delayRequestUntilDrawn*/).pipe(filter((visible) => visible), take(1), switchMap(() => this.delayRefreshUntilCustom()));
        }
        dataRequest$(options = {}) {
            // Cancel any previous request.
            this.cancelRequest$.next();
            this.optionDataStatus = 'stale';
            if (this.detailRecord) {
                this.detailRecord.deleteChildGridRecordData();
            }
            const visible$ = this.visibilityGate$(options);
            const primaryList = this.primaryDatasourceComponent;
            if (primaryList) {
                // replica should still wait until its visible before attempting to tell the primaryList to do anything
                return visible$.pipe(takeUntil(this.destroy$), takeUntil(this.cancelRequest$), switchMap(() => {
                    console.log(`Replica ${this.id} is requesting data from primary.`);
                    if (options.explicitRefresh) {
                        console.log(`Primary ${primaryList.id} is being explicitly refreshed.`);
                        return primaryList.dataRequest$({ ...options, ignoreVisibility: true });
                    }
                    if (!primaryList.delayRequestUntilVisible || primaryList.isDataPending() || primaryList.isVisible()) {
                        console.log(`Primary ${primaryList.id} serving pending data.`);
                        // avoid potential race condition. If primary list is not delayed until visible, it will refresh soon
                        return primaryList.propertyGet$('optionData').pipe(takeUntil(this.cancelRequest$), skip(1) // skip the current value because we want to wait for the fresh data that is pending
                        , filter(() => {
                            return primaryList.isDataFresh();
                        }), take(1));
                    }
                    if (primaryList.isDataFresh()) {
                        console.log(`Primary ${primaryList.id} serving fresh data.`);
                        // data is fresh, just hand it back as is 
                        return of(primaryList.optionData);
                    }
                    console.log(`Primary ${primaryList.id} has stale data, triggering refresh.`);
                    return primaryList.dataRequest$({ ...options, ignoreVisibility: true });
                }));
            }
            else if (this.baseTableName && this.record?.getChildGridRecordData(this.baseTableName, this.record.get('_id'))) {
                // the primary list will manager childGridRecordData
                if (options.clearChildGridRecordData) {
                    this.record.deleteChildGridRecordData(this.baseTableName);
                }
                else {
                    const records = this.record.getChildGridRecordData(this.baseTableName, this.record.get('_id'));
                    if (records) {
                        this.allOptionsById = {};
                        records.map((option) => {
                            this.allOptionsById[option.id] = option;
                            return option;
                        });
                        this.propertySet('optionData', records);
                        return of(records);
                    }
                }
            }
            const record = (this.parameterRecord || this.record);
            const effectiveDynamic = { ...this.dynamicParameters, ...this.baseDynamicParameters };
            if (Object.keys(effectiveDynamic).length && (!record || record.state === 'none')) {
                // maybe if refreshonparam change is OFF, we subscribe to recordState and auto refresh once the state is not "none"?
                console.log(`${this.id} - dataRequest$ skipped because it has dynamic params and recordstate is none.`);
                this.clearOptionData();
                this.propertySet('optionData', []);
                return of([]);
            }
            if (this.autoCompleteFlag && this.autoCompleteValue.length < this.autoCompleteThreshold) {
                return of([]);
            }
            if (!this.endpoint) {
                console.warn('DATA REQUEST ATTEMPTED WITH NO ENDPOINT', this.id);
                return throwError(() => new Error('Silent: No endpoint provided'));
            }
            this.allOptionsById = {};
            this.addStaticOptionsToIdMap();
            if (this.autoCompleteFlag) {
                this.clearAutoCompleteOptionData();
            }
            else {
                this.optionData = [];
            }
            // wait until visible is true and then initiate AJAX request
            let requestStartTime;
            return visible$.pipe(takeUntil(this.destroy$), switchMap(() => {
                this.optionDataStatus = 'pending';
                const parameters = this.requestParametersGet(options);
                this._lastParameters = { ...{}, ...parameters.requestData };
                if (this.paginationGUID) {
                    parameters.requestData = parameters.requestData || {};
                    parameters.requestData.deleteguid = this.paginationGUID;
                }
                // hook for grid event config in the form builder (ie. quote line items grid refresh needs to trigger section grid to refresh.)
                this.trigger('refresh');
                requestStartTime = performance.now();
                if (options.initiator) {
                    options.initiator.handleProgress(0);
                }
                if (this.endpoint.toLowerCase() === 'offlinedata') {
                    return this.offlineDataRequest$();
                }
                const connectivity = DIContainer.isRegistered('ConnectivityManager')
                    ? DIContainer.getInstance('ConnectivityManager')
                    : undefined;
                const makeRequest = () => {
                    if (this.loadingThrobber) {
                        this.loadingThrobber.offline = false;
                    }
                    return this.cacheRequest ? this.Request.GET(parameters) : this.Request.POST(parameters);
                };
                const waitForOnline$ = () => {
                    this.handleProgress(0);
                    if (this.loadingThrobber) {
                        this.loadingThrobber.offline = true;
                    }
                    return connectivity.isOnline$().pipe(filter(online => online), take(1), switchMap(() => makeRequest()));
                };
                if (connectivity && !connectivity.isOnline) {
                    if (this.cacheRequest) {
                        const cacheKey = Request.buildCacheKey(parameters);
                        const cacheManager = DIContainer.getInstance('CacheManager');
                        return cacheManager.getCache('request', cacheKey, { defaultReturnValue: false }).pipe(switchMap(cached => cached !== false ? of(cached) : waitForOnline$()));
                    }
                    return waitForOnline$();
                }
                return makeRequest();
            }), takeUntil(this.cancelRequest$), map(response => this.formatResponse(response)), tap(() => {
                DIContainer.getInstance('Application').trackTelemetry({
                    type: 'list_refresh',
                    componentId: this.id,
                    formName: this.formContext?.formName || '',
                    metadata: {
                        endpoint: this.endpoint,
                        action: this.action,
                        table: this.baseTableName || '',
                        cached: this.cacheRequest,
                        componentType: this.componentName,
                        parameters: this.allParameters,
                        durationMs: Math.round(performance.now() - requestStartTime)
                    }
                });
            }), catchError(err => {
                console.error('Error during data request:', err);
                this.handleDataRequestError(err);
                return throwError(err);
            }), 
            // Ensure that if multiple subscribers subscribe to this observable,
            // they all share the same subscription and side effects.
            share()).pipe(tap({
                complete: () => {
                    if (options.initiator) {
                        options.initiator.handleProgress(100);
                    }
                }
            }));
        }
        delayRefreshUntilCustom() {
            return of(true);
        }
        pageParametersGet() {
            return {
                guid: this.paginationGUID || ''
            };
        }
        refreshOptionRequest$(ids) {
            const parameters = this.requestParametersGet();
            if (this._lastParameters) {
                parameters.requestData = this._lastParameters;
            }
            const pageParameters = this.pageParametersGet();
            parameters.requestData = {
                ...parameters.requestData,
                ...pageParameters,
                refreshids: ids.join(',')
            };
            if (this.paginationGUID) {
                parameters.requestData = parameters.requestData || {};
                parameters.requestData.guid = this.paginationGUID;
            }
            // this was a quick solution for RC "Set Call Status" so we dont have to reload the whole console.
            // can refactor/enhance as needed as its only used in one spot for now.
            return this.Request.POST(parameters).pipe(takeUntil(this.destroy$), takeUntil(this.cancelRequest$), map(response => {
                const refreshedOptions = [];
                ids.forEach((id) => {
                    const existing = this.getOptionById(id);
                    const refreshed = response.payload[this._responseResultName].find((data) => {
                        return data[this.optionDataIdKey || this.baseTablePkName] === id;
                    });
                    forEachKey(refreshed, (value, key) => {
                        if (this.optionDataType === 'Record' && !Reflect.has(existing.changedData, key)) {
                            existing.set(key, value, 'load');
                        }
                        else if (this.optionDataType !== 'Record') {
                            existing[key] = value;
                        }
                    });
                    refreshedOptions.push(refreshed);
                });
                return refreshedOptions;
            }));
        }
        pageRequest$() {
            const parameters = this.requestParametersGet();
            if (this._lastParameters) {
                parameters.requestData = this._lastParameters;
            }
            const pageParameters = this.pageParametersGet();
            parameters.requestData = { ...parameters.requestData, ...pageParameters };
            if (this.paginationGUID) {
                parameters.requestData = parameters.requestData || {};
                parameters.requestData.guid = this.paginationGUID;
            }
            else {
                // we dont have a guid so we cant make a page request.
                return this.dataRequest$();
            }
            return this.Request.POST(parameters).pipe(takeUntil(this.destroy$), takeUntil(this.cancelRequest$), map(response => this.formatPageResponse(response)));
        }
        requestParametersGet(options = {}) {
            let params = { ...{}, ...this.allParameters };
            if (this.includeFormParameters && this.formContext) {
                const formParameters = this.formContext.parameters;
                params = { ...formParameters, ...params };
            }
            if (this.autoCompleteFlag) {
                params.autocomplete = this.autoCompleteValue;
            }
            return {
                endpoint: this.endpoint,
                action: this.action,
                loadingMessage: '',
                showErrors: false,
                cache: this.cacheRequest,
                refreshCache: options.refreshCache,
                cacheStrategy: this.cacheStrategy,
                requestData: params,
                initiator: this,
                stream: this.stream
            };
        }
        /**
         * Returns a reactive observable from the CacheManager's offlinedata store.
         * Uses `action` as the index name. Builds the query key by reading parameter values
         * whose names match the index's keyPath fields (from static/dynamic parameters).
         * For compound indexes (keyPath is an array), produces an array key.
         * The observable keeps emitting when the underlying cache changes.
         *
         * When `offlineDataJoins` is configured, enriches each row by resolving keyTemplate
         * tokens against the row's data, fetching the referenced record from the offlinedata
         * store, and merging the specified fields. Also subscribes to changes on joined records
         * so the list re-enriches reactively.
         */
        offlineDataRequest$() {
            this.cleanupOfflineDataJoinSubscriptions();
            const cacheManager = DIContainer.getInstance('CacheManager');
            const indexName = this.action;
            if (!indexName) {
                console.warn(`${this.id} - offlineDataRequest$ requires 'action' to be set as the index name`);
                return of({ payload: { [this.responseResultName]: [] } });
            }
            const keyPath = cacheManager.getIndexKeyPath('offlinedata', indexName);
            if (!keyPath) {
                console.warn(`${this.id} - offlineDataRequest$ could not find index '${indexName}' on offlinedata store`);
                return of({ payload: { [this.responseResultName]: [] } });
            }
            const key = Array.isArray(keyPath)
                ? keyPath.map(field => this.allParameters?.[field] ?? '')
                : this.allParameters?.[keyPath] ?? '';
            let isFirstEmission = true;
            // Subject that fires when a joined record changes
            const joinChange$ = new Subject();
            this.cancelRequest$.pipe(take(1)).subscribe(() => joinChange$.complete());
            return merge(cacheManager.getAllByIndex$('offlinedata', indexName, key), joinChange$.pipe(debounceTime(50), switchMap(() => cacheManager.getAllByIndex('offlinedata', indexName, key)))).pipe(takeUntil(this.cancelRequest$), switchMap(rows => {
                // Exclude records pending offline delete
                rows = rows.filter((row) => row._deleteflag !== 'Y');
                const filteredRows = this._responseFilterConditions?.length
                    ? this.applyFilterConditions(rows, this._responseFilterConditions)
                    : rows;
                if (!this._offlineDataJoins.length) {
                    return this.offlineDataEmit$(filteredRows, isFirstEmission, () => { isFirstEmission = false; });
                }
                // Resolve join keys and fetch joined records
                const joinKeys = this.resolveOfflineDataJoinKeys(filteredRows);
                if (!joinKeys.size) {
                    return this.offlineDataEmit$(filteredRows, isFirstEmission, () => { isFirstEmission = false; });
                }
                // Batch-fetch all joined records
                const keyArray = Array.from(joinKeys);
                const fetches = {};
                for (const k of keyArray) {
                    fetches[k] = cacheManager.getCache('offlinedata', k, { defaultReturnValue: undefined });
                }
                return forkJoin(fetches).pipe(map(joinedRecords => {
                    const joinedMap = new Map();
                    for (const k of keyArray) {
                        if (joinedRecords[k]) {
                            joinedMap.set(k, joinedRecords[k]);
                        }
                    }
                    this.applyOfflineDataJoins(filteredRows, joinedMap);
                    this.updateOfflineDataJoinSubscriptions(joinKeys, joinChange$);
                    return filteredRows;
                }), switchMap(enrichedRows => {
                    return this.offlineDataEmit$(enrichedRows, isFirstEmission, () => { isFirstEmission = false; });
                }));
            }));
        }
        /**
         * Handles the emission logic for offlineDataRequest$, including first-emission
         * detection and record-level diffing for subsequent emissions.
         */
        offlineDataEmit$(filteredRows, isFirstEmission, markEmitted) {
            if (isFirstEmission || this.optionDataType !== 'Record') {
                markEmitted();
                return of({ payload: { [this.responseResultName]: filteredRows } });
            }
            // Diff against existing optionData
            if (filteredRows.length !== this.optionData.length) {
                return of({ payload: { [this.responseResultName]: filteredRows } });
            }
            const existingById = {};
            this.optionData.forEach(record => {
                existingById[record.get('_id')] = record;
            });
            for (const row of filteredRows) {
                const existing = existingById[row._id];
                if (!existing) {
                    return of({ payload: { [this.responseResultName]: filteredRows } });
                }
                for (const col of Object.keys(row)) {
                    if (existing.get(col) !== row[col]) {
                        existing.set(col, row[col], 'load');
                    }
                }
            }
            return EMPTY;
        }
        /**
         * Resolves all distinct join keys from the current rows and join configs.
         */
        resolveOfflineDataJoinKeys(rows) {
            const keys = new Set();
            for (const row of rows) {
                for (const join of this._offlineDataJoins) {
                    const key = replaceTokens(join.keyTemplate, row, { keepMissingTokens: true });
                    if (key && key !== join.keyTemplate) {
                        keys.add(key);
                    }
                }
            }
            return keys;
        }
        /**
         * Manages reactive subscriptions to joined record changes.
         * Diffs current keys against previously-watched keys, subscribing to new ones
         * and unsubscribing from removed ones.
         */
        updateOfflineDataJoinSubscriptions(newKeys, joinChange$) {
            const broadcastManager = DIContainer.getInstance('BroadcastManager');
            const prevKeys = this._offlineDataJoinKeys;
            // Find keys that are no longer needed
            const removedKeys = new Set();
            for (const k of prevKeys) {
                if (!newKeys.has(k))
                    removedKeys.add(k);
            }
            // Find newly-added keys
            const addedKeys = new Set();
            for (const k of newKeys) {
                if (!prevKeys.has(k))
                    addedKeys.add(k);
            }
            // Unsubscribe from removed keys
            if (removedKeys.size) {
                // Tear down all and rebuild — simpler than tracking per-key subscriptions
                this._offlineDataJoinSubscriptions.forEach(sub => sub.unsubscribe());
                this._offlineDataJoinSubscriptions = [];
                // Re-subscribe to all current keys
                for (const k of newKeys) {
                    const eventName = `cache_change:offlinedata:${k}`;
                    const sub = broadcastManager.listen(eventName).pipe(takeUntil(this.cancelRequest$)).subscribe(() => joinChange$.next());
                    this._offlineDataJoinSubscriptions.push(sub);
                }
            }
            else if (addedKeys.size) {
                // Only new keys — append subscriptions
                for (const k of addedKeys) {
                    const eventName = `cache_change:offlinedata:${k}`;
                    const sub = broadcastManager.listen(eventName).pipe(takeUntil(this.cancelRequest$)).subscribe(() => joinChange$.next());
                    this._offlineDataJoinSubscriptions.push(sub);
                }
            }
            this._offlineDataJoinKeys = newKeys;
        }
        applyOfflineDataJoins(rows, joinedRecordsById) {
            for (const row of rows) {
                for (const join of this._offlineDataJoins) {
                    const key = replaceTokens(join.keyTemplate, row);
                    const joinedRecord = joinedRecordsById.get(key);
                    if (joinedRecord) {
                        if (join.mergeFields.length === 1 && join.mergeFields[0] === '*') {
                            for (const field of Object.keys(joinedRecord)) {
                                if (!(field in row)) {
                                    row[field] = joinedRecord[field];
                                }
                            }
                        }
                        else {
                            for (const field of join.mergeFields) {
                                row[field] = joinedRecord[field];
                            }
                        }
                    }
                }
            }
        }
        cleanupOfflineDataJoinSubscriptions() {
            this._offlineDataJoinSubscriptions.forEach(sub => sub.unsubscribe());
            this._offlineDataJoinSubscriptions = [];
            this._offlineDataJoinKeys.clear();
        }
        addStaticOptionsToIdMap() {
            if (this.staticOptionData.length) {
                this.staticOptionData.forEach((option) => {
                    this.allOptionsById[option._id] = option;
                });
            }
        }
        renderOptionData(_optionData) {
            console.error('Implement renderOptionData function in ' + this.componentName + '.ts');
        }
        getOptionById(id) {
            id = id.trim();
            return this.allOptionsById[id];
        }
        getOptionByType(optionType, id) {
            return this.resultSetMapping?.[optionType]?.optionsById?.[id];
        }
        propertiesLoad(properties) {
            if (properties.resultSetMapping) {
                properties.resultSetMapping = {
                    ...this._resultSetMapping,
                    ...properties.resultSetMapping
                };
            }
            super.propertiesLoad(properties);
            // need to set this up before draw so that parameters are ready if we need to force a programmatic refresh from another tab, ex. edit quote branch
            this.setupAutomaticDataRequest();
        }
        /**
         * note: set the dynamic/static parameters prior to draw being called
         */
        draw() {
            super.draw();
            this.optionsStreamGet$()
                .subscribe((optionData) => {
                this.renderOptionData(optionData);
            });
            if (this.primaryDatasourceId || this.dependencies.primaryDatasourceComponent) {
                this.primaryDatasourceComponent$.subscribe((primaryList) => {
                    if (primaryList) {
                        primaryList.setupAutomaticDataRequest();
                    }
                });
                this.setupSyncPrimaryDatasourceProperties();
            }
            this.setupAutomaticDataRequest();
            if (this.multiSelect) {
                this.renderMultiSelect();
            }
        }
        setupSyncPrimaryDatasourceProperties() {
            this.primaryDatasourcePropertiesSubscription?.unsubscribe();
            this.primaryDatasourcePropertiesSubscription = this.primaryDatasourceComponent$
                .subscribe((component) => {
                if (!component)
                    return;
                component.propertyGet$('progress').subscribe((progress) => this.handleProgress(progress));
                [
                    'endpoint',
                    'action',
                    'dynamicParameters',
                    'baseDynamicParameters',
                    'staticParameters',
                    'dataRequestOnRecordLoad',
                    'dataRequestOnParameterChange',
                    'dataRequestOnInit',
                    'delayRequestUntilVisible',
                    'parameterRecord',
                    'responseResultName',
                    'cacheRequest'
                    // ,''
                ].forEach((prop) => {
                    component.propertyGet$(prop).subscribe((propval) => this.propertySet(prop, propval));
                });
            });
        }
        setupAutomaticDataRequest() {
            if (this.autoRefreshSetupCompleted) {
                return;
            }
            this.allParametersStreamGet$()
                .subscribe((allParameters) => {
                this.propertySet('allParameters', allParameters);
            });
            this.dataRequestStreamGet$()
                .subscribe(() => {
                this.refreshData();
            });
            this.autoRefreshSetupCompleted = true;
        }
        /**
         * Stream that computes allParameters based on dynamic parameters
         */
        allParametersStreamGet$() {
            return combineLatest([
                this.propertyGet$('parameterRecord').pipe(startWith(false)),
                this.propertyGet$('dynamicParameters').pipe(startWith(false)),
                this.propertyGet$('staticParameters').pipe(startWith(false)),
                this.propertyGet$('parameterRecord').pipe(startWith(false), switchMap((r) => !r ? 'none' : r.propertyGet$('state')), map((state) => {
                    return state !== 'none';
                }), distinctUntilChanged()),
                this.propertyGet$('baseDynamicParameters').pipe(startWith(false))
            ]).pipe(takeUntil(this.destroy$), filter(([record, dynamic, _staticParams, _state, baseDynamic]) => !((dynamic || baseDynamic) && !record)), switchMap(([record, dynamic, staticParams, state, baseDynamic]) => {
                const effectiveDynamic = { ...(dynamic || {}), ...(baseDynamic || {}) };
                const hasDynamic = Object.keys(effectiveDynamic).length > 0;
                const observables = hasDynamic
                    ? Object.keys(effectiveDynamic).map(key => !state ? of({ key, value: undefined }) : record.get$(effectiveDynamic[key], { distinct: true }).pipe(map(value => ({ key, value }))))
                    : [];
                return observables.length > 0
                    ? combineLatest(observables).pipe(map(pairs => {
                        const dynamicParameters = pairs.reduce((acc, pair) => {
                            if (typeof pair.value === 'undefined') {
                                acc[pair.key] = !state ? undefined : '';
                            }
                            else {
                                acc[pair.key] = pair.value || '';
                            }
                            return acc;
                        }, {});
                        return { ...staticParams, ...dynamicParameters };
                    }))
                    : of({ ...staticParams });
            }), distinctUntilChanged((prevParameters, currParameters) => {
                return JSON.stringify(prevParameters) === JSON.stringify(currParameters);
            }));
        }
        /**
         * Stream that decides when to make a data request
         */
        dataRequestStreamGet$() {
            return combineLatest([
                this.propertyGet$('dataRequestOnInit'),
                this.propertyGet$('dataRequestOnRecordLoad'),
                this.propertyGet$('dataRequestOnParameterChange'),
                this.propertyGet$('endpoint'),
                this.propertyGet$('autoCompleteFlag')
            ]).pipe(takeUntil(this.destroy$), filter(([onInit, onLoad, onParamChange, endpoint, _autoCompleteFlag]) => {
                return !!(endpoint && (onInit || onParamChange || onLoad));
            }), switchMap((value) => {
                const [_onInit, onLoad, onParamChange, _endpoint] = value;
                if (onLoad) {
                    return this.propertyGet$('parameterRecord')
                        .pipe(takeUntil(this.destroy$), filter((record) => !!record), switchMap((record) => record.load$) // whenever it loads we need to refresh
                    , switchMap(() => {
                        if (onParamChange) {
                            return this.propertyGet$('allParameters').pipe(debounceTime(5));
                        }
                        else {
                            return this.propertyGet$('allParameters').pipe(take(1));
                        }
                    }));
                }
                else if (onParamChange) {
                    return this.propertyGet$('allParameters').pipe(debounceTime(5)); // avoid multiple refreshes from rapid parameter changes 
                }
                else /*onInit*/ {
                    return this.propertyGet$('allParameters').pipe(take(1));
                }
            }), switchMap(() => {
                this.optionDataStatus = 'stale';
                return this.autoCompleteDelay$();
            }));
        }
        autoCompleteDelay$() {
            return !this.autoCompleteFlag ? of(true) : this.propertyGet$('autoCompleteValue').pipe(filter((value) => {
                return this.checkAutoCompleteFilter(value);
            }), debounceTime(200), map(() => true));
        }
        checkAutoCompleteFilter(filter) {
            if ((filter || '').length <= this.autoCompleteThreshold) {
                this.clearAutoCompleteOptionData();
            }
            return (filter || '').length >= this.autoCompleteThreshold;
        }
        optionRemove(id, preserveRecord, optionType) {
            if (typeof id === 'string') {
                id = [id];
            }
            let optionById = this.allOptionsById;
            let optionData = this.optionData;
            let optionDataType = this.optionDataType;
            if (optionType) {
                optionData = this.resultSetMapping[optionType].optionData || [];
                optionById = this.resultSetMapping[optionType].optionsById || {};
                optionDataType = this.resultSetMapping[optionType].optionDataType;
            }
            id.forEach((id) => {
                const option = optionById[id];
                if (this.currentRecord === option) {
                    this.currentRecord = undefined;
                }
                if (!preserveRecord) {
                    delete optionById[id];
                    if (option instanceof DataRecord) {
                        option.destroy();
                    }
                }
            });
            const filteredOptionData = optionDataType === 'Record' ?
                optionData.filter((o) => id.indexOf(o.id) === -1)
                : optionData.filter((o) => id.indexOf(o._id) === -1);
            if (!optionType) {
                if (this.sequenceColumn) {
                    this.optionData = this.sequenceResponseData(filteredOptionData);
                }
                else {
                    this.optionData = filteredOptionData;
                }
            }
        }
        optionsGet(includeStatic = true) {
            const staticOptions = this.staticOptionDataGet();
            const resultOptions = this.optionData;
            if (!includeStatic) {
                return resultOptions;
            }
            const combinedOptions = [...staticOptions, ...resultOptions];
            return combinedOptions;
        }
        staticOptionDataGet() {
            return this.staticOptionData;
        }
        optionsStreamGet$(includeStatic = true) {
            return combineLatest([
                this.propertyGet$('optionData'),
                this.propertyGet$('staticOptionData'),
                this.primaryDatasourceComponent$.pipe(startWith(this.primaryDatasourceComponent), switchMap((component) => {
                    if (!component) {
                        return of([]);
                    }
                    else {
                        if (this.syncPrimaryDatasourceFilters) {
                            return component.filteredOptionData$;
                        }
                        else {
                            return component.propertyGet$('optionData');
                        }
                    }
                }))
            ])
                .pipe(takeUntil(this.destroy$), map((combined) => {
                let optionData = combined[0];
                if (this.primaryDatasourceComponent) {
                    optionData = combined[2];
                    this._optionData = optionData; // store a reference to the optionData array in this replica list for easy access
                    this.updateAllOptionsById(optionData);
                }
                if (!includeStatic) {
                    return optionData;
                }
                return [...combined[1], ...optionData];
            }));
        }
        applyFilterConditions(optionData, filterConditions) {
            if (!filterConditions || !filterConditions.length)
                return optionData;
            return optionData.filter(record => {
                const get = (col) => record && typeof record.get === 'function' ? record.get(col) : record?.[col];
                return filterConditions.every(f => {
                    if (!f.column)
                        return true;
                    let raw = get(f.column);
                    if (raw == null)
                        raw = '';
                    let value = f.value;
                    if (this.recordStore) {
                        value = this.recordStore.replaceTokens(value);
                    }
                    else {
                        value = replaceTokens(value);
                    }
                    let lhs;
                    let rhs;
                    let inList = [];
                    if (f.type === 'DATE') {
                        if (!raw)
                            return false;
                        const tok = value; //TOKEN(f.value) ? (TOKEN(f.value) as string) : f.value as string;
                        const diff = DateTimeManager.dateDiff(tok, DateTimeManager.formatDate(raw), 'days');
                        switch (f.operator) {
                            case '=':
                            case 'IN':
                            case 'LIKE': return diff === 0;
                            case '!=':
                            case 'NOT IN':
                            case 'NOT LIKE': return diff !== 0;
                            case '>': return diff > 0;
                            case '<': return diff < 0;
                            case '>=': return diff >= 0;
                            case '<=': return diff <= 0;
                            default: return false;
                        }
                    }
                    if (f.type === 'NUMERIC') {
                        lhs = Number(raw) || 0;
                        rhs = Number(value) || 0;
                    }
                    else {
                        const tok = value; //TOKEN(f.value) ? (TOKEN(f.value) as string) : f.value as string;
                        lhs = String(raw).toLowerCase();
                        rhs = String(tok).toLowerCase();
                    }
                    if (f.operator === 'IN' || f.operator === 'NOT IN') {
                        inList = String(value).split(',').map(item => {
                            const tok = TOKEN(item.trim()) ? TOKEN(item.trim()) : item.trim();
                            return f.type === 'NUMERIC' ? Number(tok) || 0 : tok.toLowerCase();
                        });
                    }
                    switch (f.operator) {
                        case '=': return lhs === rhs;
                        case '!=': return lhs !== rhs;
                        case 'LIKE': return String(lhs).includes(String(rhs));
                        case 'NOT LIKE': return !String(lhs).includes(String(rhs));
                        case '>': return lhs > rhs;
                        case '<': return lhs < rhs;
                        case '>=': return lhs >= rhs;
                        case '<=': return lhs <= rhs;
                        case 'IN': return inList.includes(lhs);
                        case 'NOT IN': return !inList.includes(lhs);
                        default: return false;
                    }
                });
            });
        }
        updateAllOptionsById(optionData) {
            this.allOptionsById = {};
            this.addStaticOptionsToIdMap();
            optionData.forEach((option) => {
                const id = (option instanceof DataRecord) ? option.get('_id') : option._id;
                this.allOptionsById[id] = option;
            });
        }
        staticParamAdd(paramName, paramValue) {
            const params = this.staticParameters || {};
            params[paramName] = paramValue;
            this.staticParameters = params;
        }
        dynamicParamAdd(paramName, paramColumnName, base) {
            if (base) {
                const params = this.baseDynamicParameters || {};
                params[paramName] = paramColumnName;
                this.baseDynamicParameters = params;
            }
            else {
                const params = this.dynamicParameters || {};
                params[paramName] = paramColumnName;
                this.dynamicParameters = params;
            }
        }
        staticParamRemove(paramName) {
            const params = this.staticParameters || {};
            delete params[paramName];
            if (Object.keys(params).length === 0) {
                this.staticParameters = undefined;
            }
            else {
                this.staticParameters = params;
            }
        }
        dynamicParamRemove(paramName) {
            const dynamic = this.dynamicParameters || {};
            if (paramName in dynamic) {
                delete dynamic[paramName];
                this.dynamicParameters = Object.keys(dynamic).length === 0 ? undefined : dynamic;
            }
            const base = this.baseDynamicParameters || {};
            if (paramName in base) {
                delete base[paramName];
                this.baseDynamicParameters = Object.keys(base).length === 0 ? undefined : base;
            }
        }
        linkRecord(record) {
            super.linkRecord(record);
            if (!this._parameterRecord) {
                this.parameterRecord = record;
            }
        }
        unlinkRecord(keepState = false) {
            if (this._parameterRecord && this.record && this.record.id === this._parameterRecord.id) {
                this.parameterRecord = undefined;
            }
            super.unlinkRecord(keepState);
        }
        linkComponents(components) {
            if (this.linkComponentsSubscription) {
                this.linkComponentsSubscription.unsubscribe();
            }
            // scan components to see if we need to load any additional data when the record changes (ie. fields that are not in our grid)
            this.linkComponentsSubscription = this.propertyGet$('detailRecord')
                .subscribe({
                next: (record) => {
                    if (record) {
                        components.forEach((component) => {
                            if (record !== component.record) {
                                component.linkRecord(record);
                            }
                        });
                    }
                    else {
                        components.forEach((component) => {
                            component.unlinkRecord();
                        });
                    }
                },
                complete: () => {
                    // in case we call linkComponents a second time, we need to unlink the previous set of components first (as some of the components might no longer be in the new array)
                    components.forEach((component) => {
                        component.unlinkRecord();
                    });
                }
            });
        }
        handleCurrentRecordChanged(record) {
            // goal here is to not switch grid records in "add" state to "edit" prematurely because then revert precreated doesnt work.
            if (record && (record.state !== 'add' || this.recordState === 'read')) {
                record.state = this.recordState === 'add' ? 'edit' : this.recordState; // dont assign add state to grid records if they werent already in add state
            }
            this._delayCurrentRecordDetailSyncSubscription?.unsubscribe();
            if (this.detailRecord?.inProgress) {
                this._delayCurrentRecordDetailSyncSubscription = this.detailRecord.propertyGet$('progress').pipe(filter((p) => p === 100), take(1)).subscribe(() => {
                    // this is for a scenario where a component (AccountNoModalSelect) is doing async validation that is not yet completed
                    // and the user changes their current record in the line items grid
                    // we must ensure the validation completes and sets the account on the original record before we shift to the new row.
                    this.setupSyncDetailRecordChanges();
                });
            }
            else {
                this.setupSyncDetailRecordChanges();
            }
        }
        handleRecordStateChange(state) {
            if (this.currentRecord && (this.currentRecord.state !== 'add' || state === 'read')) {
                this.currentRecord.state = state === 'add' ? 'edit' : state; // dont assign add state to grid records if they werent already in add state
            }
            super.handleRecordStateChange(state);
        }
        setupSyncDetailRecordChanges() {
            if (!this.syncDetailSubject) {
                this.syncDetailSubject = new Subject();
            }
            this.syncDetailSubject.next();
            const retainedData = {};
            this.detailRecord?.q360formrecordvalue?.forEach((formRecordValue) => {
                retainedData[formRecordValue.columnname] = this.detailRecord.get(formRecordValue.columnname);
            });
            if (!this.currentRecord || !this.detailRecord) {
                this.detailRecord?.load(retainedData);
                if (this.detailRecord) {
                    this.detailRecord.state = 'none';
                }
                return;
            }
            const currentRecord = this.currentRecord;
            const detailRecord = this.detailRecord;
            if (currentRecord.tableName) {
                detailRecord.tableName = currentRecord.tableName;
                detailRecord.pkColumnName = currentRecord.pkColumnName;
            }
            detailRecord.listMixinGlobalId = currentRecord.listMixinGlobalId;
            detailRecord.load({ ...currentRecord.data, ...retainedData });
            if (detailRecord.defaultState === 'none') {
                // sync with the current records state
                currentRecord.propertyGet$('state')
                    .pipe(takeUntil(this.syncDetailSubject), takeUntil(this.destroy$))
                    .subscribe((state) => {
                    if (detailRecord) {
                        detailRecord.state = state;
                    }
                });
            }
            else if (detailRecord.defaultState) {
                detailRecord.state = detailRecord.defaultState;
            }
            detailRecord.allChangesGet$()
                .pipe(takeUntil(this.syncDetailSubject), takeUntil(this.destroy$))
                .subscribe(({ columnName, value, source }) => {
                if (currentRecord
                    && source !== 'load'
                    && source !== 'currentsync' // avoid infinite loop scenario that was happening in line items grid when editing line_no of a row to match another row
                    && value != currentRecord.get(columnName)) {
                    currentRecord.set(columnName, value, 'detailsync');
                }
            });
            currentRecord.allChangesGet$()
                .pipe(takeUntil(this.syncDetailSubject), takeUntil(this.destroy$))
                .subscribe(({ columnName, value, source }) => {
                if (detailRecord
                    && source !== 'detailsync' // avoid infinite loop scenario that was happening in line items grid when editing line_no of a row to match another row
                    && source !== 'load'
                    && value != detailRecord.get(columnName)) {
                    detailRecord.set(columnName, value, 'currentsync');
                }
            });
            currentRecord.refreshDisabledState$
                .pipe(takeUntil(this.syncDetailSubject), takeUntil(this.destroy$))
                .subscribe(() => {
                detailRecord?.refreshDisabledState$.next();
            });
        }
        programmaticResponse(response, metaData) {
            return this.formatResponse({
                payload: {
                    [this.responseResultName]: response,
                    [`${this.responseResultName}MetaData`]: metaData
                }
            });
        }
        mergeAutoCompleteOptionDataWithResponse(optionData) {
            optionData = [...this.optionData, ...optionData];
            return optionData;
        }
        parseResponse(response, _options = {}) {
            let optionData = [];
            let metaData = [];
            if (Reflect.has(response.payload, this.responseResultName)) {
                optionData = response.payload[this.responseResultName];
                metaData = response.payload[`${this.responseResultName}MetaData`];
            }
            else if (Reflect.has(response.payload, this.alternateResultName)) {
                optionData = response.payload[this.alternateResultName];
                metaData = response.payload[`${this.alternateResultName}MetaData`];
            }
            else if (response.payload._recordset?.length) {
                const key = response.payload._recordset[0]._recordset;
                if (key !== 'status') {
                    optionData = response.payload[key];
                    metaData = response.payload[`${key}MetaData`];
                }
            }
            else if (this.responseResultName === 'result' && this.alternateResultName === 'griddata') {
                const key = Object.keys(response.payload)[0];
                if (key !== 'status' && key !== '_recordset' && key !== 'outvars') {
                    optionData = response.payload[key];
                    metaData = response.payload[`${key}MetaData`];
                }
            }
            return { optionData, metaData };
        }
        formatResponse(response) {
            // eslint-disable-next-line prefer-const
            let { optionData, metaData } = this.parseResponse(response);
            this.propertySet('metaData', metaData);
            // remove old option data
            if (this.autoCompleteFlag) {
                this.clearAutoCompleteOptionData();
                optionData = this.mergeAutoCompleteOptionDataWithResponse(optionData);
            }
            else {
                this.clearOptionData();
            }
            if (this._resultSetMapping) {
                forEachKey(this._resultSetMapping, (value, key) => {
                    value.optionsById = {};
                    if (response.payload[value.resultName]) {
                        value.optionData = response.payload[value.resultName] || [];
                    }
                    else {
                        value.optionData = [];
                    }
                    // if (value.optionDataType === 'Record' && value.optionData?.length) {
                    value.optionData = value.optionData.map((data) => {
                        const id = this.addOptionId(data, key);
                        if (value.optionDataType === 'Record') {
                            const r = this.recordizeOption(data, key);
                            value.optionsById[r.id] = r;
                            return r;
                        }
                        data._id = id;
                        value.optionsById[data._id] = data;
                        return data;
                    });
                    // }
                });
            }
            // apply responseFilterConditions before recordizing — defines what optionData IS (e.g. offline/IndexedDB subset)
            if (this._responseFilterConditions?.length) {
                optionData = this.applyFilterConditions(optionData, this._responseFilterConditions);
            }
            optionData = optionData.map((option, index) => {
                const id = this.addOptionId(option);
                if (this.optionDataType === 'Record') {
                    option = this.recordizeOption(option);
                    option.index = index;
                }
                this.allOptionsById[id] = option;
                return option;
            });
            if (response.payload?.outvars?.guid) {
                this.paginationGUID = response.payload.outvars.guid;
            }
            this.trigger('response', { async: false }, optionData);
            this.optionDataStatus = 'fresh';
            this.propertySet('optionData', optionData);
            if (this.optionDataType === 'Record') {
                this.addChildRecordDataToDetail(optionData);
            }
            if (this.sequenceColumn) {
                // separate loop is mostly a needed hook for GridTree to handle bad data in responses, otherwise we could do this in the loop above.. 
                this.sequenceResponseData(optionData);
            }
            return optionData;
        }
        sequenceResponseData(optionData) {
            if (!this.sequenceColumn) {
                return optionData;
            }
            if (this.optionDataType === 'Record') {
                optionData.forEach((option, index) => {
                    if (Number(option.get(this.sequenceColumn)) !== index + 1) {
                        option.set(this.sequenceColumn, index + 1, 'calculation');
                    }
                });
            }
            else {
                optionData.forEach((option, index) => {
                    option[this.sequenceColumn] = index + 1;
                });
            }
            return optionData;
        }
        addChildRecordDataToDetail(optionData) {
            if (this.baseTableName && this.record?.recordType === 'detail') {
                this.record?.addChildGridRecordData(this.baseTableName, this.record.get('_id'), optionData);
            }
        }
        // merge our data as a default behavior, other components can decide how to handle pages. This was added for the scheduler specifically 
        formatPageResponse(response) {
            const { optionData } = this.parseResponse(response, { pageResponse: true });
            const realOptionData = this.optionData;
            optionData.forEach((option, index) => {
                const id = this.addOptionId(option);
                if (!this.allOptionsById[id]) {
                    if (this.optionDataType === 'Record') {
                        option = this.recordizeOption(option);
                        option.index = index;
                    }
                    this.allOptionsById[id] = option;
                    realOptionData.push(option);
                }
            });
            if (response.payload?.outvars?.guid) {
                this.paginationGUID = response.payload.outvars.guid;
            }
            this.propertySet('optionData', realOptionData);
            return realOptionData;
        }
        clearOptionData() {
            if (this.optionDataType === 'Record') {
                this._optionData?.forEach((r) => r.destroy());
                this.deletedData.forEach((r) => r.destroy());
                this.deletedData.length = 0;
            }
            if (this._resultSetMapping) {
                forEachKey(this._resultSetMapping, (value, key) => {
                    if (value.optionDataType === 'Record') {
                        value.optionData?.forEach((r) => r.destroy());
                    }
                    value.optionsById = {};
                    value.optionData = [];
                });
            }
            this.optionData = [];
        }
        addOptionId(option, optionType) {
            if (option instanceof DataRecord) {
                return option.get('_id');
            }
            if (Reflect.has(option, '_id') && typeof option['_id'] !== 'undefined') {
                return option['_id'];
            }
            let idKey = this.optionDataIdKey;
            if (optionType) {
                idKey = this.resultSetMapping[optionType].baseTablePkName;
            }
            let id = (idKey ? option[idKey] : option['_id']) /* || option['_id']*/;
            if (typeof id !== 'string') {
                id = getID('list');
            }
            else {
                id = id.trim();
            }
            option._id = id;
            return id;
        }
        recordizeOption(option, optionType) {
            if (optionType) {
                const r = new DataRecord(option._id, { /*listMixinGlobalId: this.globalId,*/ formGlobalId: this.formContext?.globalId });
                // r.changeSubject = this._changeSubject!
                r.tableName = this._resultSetMapping[optionType].baseTableName;
                r.pkColumnName = this._resultSetMapping[optionType].baseTablePkName;
                r.load(option);
                return r;
            }
            const r = new DataRecord(option._id, { listMixinGlobalId: this.globalId, formGlobalId: this.formContext?.globalId });
            r.changeSubject = this._changeSubject;
            r.tableName = this.baseTableName;
            r.pkColumnName = this.baseTablePkName;
            r.load(option);
            return r;
        }
        formatStaticOptionData(option) {
            return option;
        }
        insert(rowData, options = {}) {
            if (this.primaryDatasourceComponent) {
                return this.primaryDatasourceComponent.insert(rowData, options);
            }
            let optionData = this.optionData;
            let optionDataType = this.optionDataType;
            let optionsById = this.allOptionsById;
            if (options.optionType) {
                optionData = this._resultSetMapping[options.optionType].optionData || [];
                optionDataType = this._resultSetMapping[options.optionType].optionDataType;
                optionsById = this._resultSetMapping[options.optionType].optionsById || {};
            }
            const added = [];
            // Determine the insertion index based on the new row's provided sequence value.
            // Default: append at the end.
            let insertIndex = optionData.length;
            let desiredSequence = optionData.length + 1; // sequence = index + 1.
            if (rowData.length && this.sequenceColumn && rowData[0][this.sequenceColumn] && !options.optionType) {
                // Sort new rows if a sequence is provided.
                rowData.sort((a, b) => Number(a[this.sequenceColumn]) - Number(b[this.sequenceColumn]));
                desiredSequence = Number(rowData[0][this.sequenceColumn]);
                // Convert 1-indexed desiredSequence to 0-indexed insertIndex.
                insertIndex = Math.min(Math.max(desiredSequence - 1, 0), optionData.length);
            }
            // Process new rows: assign sequence (and index for Record types).
            const newRows = rowData.map((option, i) => {
                const id = this.addOptionId(option, options.optionType);
                if (this.sequenceColumn && !options.optionType) {
                    // New rows get consecutive sequence numbers starting at desiredSequence.
                    option[this.sequenceColumn] = desiredSequence + i;
                }
                if (optionDataType === 'Record' && !(option instanceof DataRecord)) {
                    option = this.recordizeOption(option, options.optionType);
                    // Set index based on the future array position.
                    option.index = insertIndex + i;
                    const precreated = options.precreated ||
                        (typeof options.precreated === 'undefined' && !!option.data.pg_guid);
                    if (!precreated) {
                        option.changedData = { ...option.data };
                    }
                    else if (option.data.pg_guid) {
                        option.changedData.pg_guid = option.data.pg_guid;
                    }
                }
                if (optionDataType === 'Record') {
                    option.state = 'add';
                    option.revertToken = options.revertToken;
                }
                added.push(option);
                optionsById[id] = option;
                return option;
            });
            // Splice new rows into optionData at the computed insertion index.
            optionData.splice(insertIndex, 0, ...newRows);
            // Update the sequence for all rows from the insertion point onward,
            // trusting the order in optionData. This loop runs only for rows affected by the insert.
            if (this.sequenceColumn && !options.optionType) {
                for (let i = insertIndex; i < optionData.length; i++) {
                    const newSeq = i + 1; // Sequence is 1-indexed.
                    if (optionDataType === 'Record') {
                        if (Number(optionData[i].get(this.sequenceColumn)) !== newSeq) {
                            optionData[i].set(this.sequenceColumn, newSeq, 'calculation');
                        }
                        if (optionData[i].index !== undefined) {
                            optionData[i].index = i;
                        }
                    }
                    else {
                        optionData[i][this.sequenceColumn] = newSeq;
                        if (optionData[i].index !== undefined) {
                            optionData[i].index = i;
                        }
                    }
                }
            }
            // Update the data to trigger redraw.
            if (!options.optionType) {
                this.optionData = optionData;
            }
            return added;
        }
        changedRecordsGet(options = {}) {
            let records = [];
            if (this._resultSetMapping) {
                forEachKey(this._resultSetMapping, (mapping, key) => {
                    if (mapping.optionDataType === 'Record' && mapping.optionData) {
                        mapping.optionData.forEach((record) => {
                            if (Object.keys(record.changedData).length
                                || (options?.includeAdded && record.state === 'add')) {
                                records.push(record);
                            }
                        });
                    }
                });
            }
            if (this.optionDataType !== 'Record') {
                return records;
            }
            if (this.record?.recordType === 'detail' && this.record.childGridRecordData && this.record.childGridRecordData[this.baseTableName]) {
                forEachKey(this.record.childGridRecordData[this.baseTableName], (children) => {
                    children.forEach((record) => {
                        if (Object.keys(record.changedData).length) {
                            records.push(record);
                        }
                    });
                });
            }
            else {
                this.optionData.forEach((record) => {
                    if (Object.keys(record.changedData).length
                        || (options?.includeAdded && record.state === 'add')) {
                        records.push(record);
                    }
                });
            }
            if (options.includeDetailRecordChanges && this.detailRecord && this.detailRecord.childGridRecordData) {
                forEachKey(this.detailRecord.childGridRecordData, (gridData, tablename) => {
                    forEachKey(gridData, (children, id) => {
                        children.forEach((record) => {
                            if (Object.keys(record.changedData).length) {
                                records.push(record);
                            }
                        });
                    });
                });
            }
            if (options.includeDeleted && this.deletedData.length) {
                records = records.concat(this.deletedData);
            }
            return records;
        }
        handleChangesSaved() {
            this.changedRecordsGet({ includeDeleted: true, includeDetailRecordChanges: true }).forEach((record) => {
                if (record.deleteFlag === 'Y') {
                    record.destroy();
                }
                else {
                    record.changedData = {};
                    record.propertySet('_loadedData', {});
                }
            });
            this.deletedData.length = 0;
        }
        handleDataRequestError(error) {
            if (!error.displayed) {
                this.Request.showErrors(error);
            }
            this.handleErrorResponse(error);
        }
        handleErrorResponse(errors) {
            this.clearRecordErrors();
            if (this.optionDataType !== 'Record' || !errors.length) {
                return;
            }
            const recordsWithErrors = new Set();
            errors.forEach((error) => {
                if (!error.componentid) {
                    return;
                }
                const record = this.getOptionById(error.componentid);
                if (!record) {
                    return;
                }
                record.saveValidationErrors.push(error);
                recordsWithErrors.add(record);
            });
            recordsWithErrors.forEach((record) => {
                this.renderRecordErrors(record);
            });
        }
        /**
         * Renders error messages for a record if there are save validation errors.
         * Note: visualizing errors must be implemented by subclasses
         * @param {DataRecord} record - The record for which error messages need to be rendered.
         * @return {void}
         */
        renderRecordErrors(record) {
            if (record.deleteFlag === 'Y' && record.saveValidationErrors.length) {
                record.deleteFlag = 'N';
                const index = record.index;
                if (typeof index === 'number') {
                    this.optionData.splice(index, 0, record);
                }
                else {
                    this.optionData.push(record);
                }
                this.deletedData = this.deletedData.filter((a) => a !== record);
                // eslint-disable-next-line no-self-assign
                this.optionData = this.optionData;
            }
        }
        clearRecordErrors() {
            if (this.optionDataType !== 'Record') {
                return;
            }
            this.optionData.forEach((r) => {
                if (r.saveValidationErrors.length) {
                    r.saveValidationErrors.length = 0;
                    this.renderRecordErrors(r);
                }
            });
        }
        requestPackageGet(options = {}) {
            options = { ...{ changedDataOnly: true }, ...options }; // TODO: actually implement this flag if needed
            if (!options.includeReplica && this.primaryDatasourceComponent) {
                return []; // the primary will submit the data in most scenarios
            }
            let data = [];
            if (this.optionDataType === 'Record') {
                const clearErrors = !!options.clearValidationErrors;
                this.changedRecordsGet({
                    includeDeleted: true,
                    includeAdded: options.includeAdded,
                    includeDetailRecordChanges: options.includeDetailRecordChanges
                }).forEach((record) => {
                    if (clearErrors && record.saveValidationErrors.length) {
                        record.saveValidationErrors.length = 0;
                        this.renderRecordErrors(record);
                    }
                    data.push(record.requestPackageGet(options));
                });
                // data = data.concat(this.deletedData);
            }
            else {
                // dont think this hook is used currently, editable lists are always using Record data type
                this.optionData.forEach((option) => {
                    data.push(option);
                });
                data = data.concat(this.deletedData);
            }
            return data;
        }
        /**
         * Delete an array of records from the grid
         * @param recordsToDelete
         */
        deleteRows(recordsToDelete, optionType) {
            this.deleteRows$(recordsToDelete, optionType).subscribe();
        }
        /**
         * A subscribable version of deleteRows that returns an observable which emits once the deletion is completed.
         * @param recordsToDelete
         */
        deleteRows$(recordsToDelete, optionType) {
            if (this.primaryDatasourceComponent) {
                // tell primary to delete
                return this.primaryDatasourceComponent.deleteRows$(recordsToDelete, optionType);
            }
            const previewDelete = ['read', 'none'].indexOf(this.recordState) === -1;
            const ctx = eventContextGet({
                initiator: this,
                records: recordsToDelete
            });
            let baseTable = this.baseTableName;
            if (optionType) {
                baseTable = this.resultSetMapping[optionType].baseTableName;
            }
            return DIContainer.getRecordLogicClass(baseTable).pipe(switchMap(bizzClass => bizzClass.delete(ctx, recordsToDelete, { previewDelete: previewDelete })), tap(() => {
                if (previewDelete) {
                    recordsToDelete.forEach((record) => {
                        if (record.pkValue) {
                            this.deletedData.push(record);
                        }
                    });
                }
                // for now, instead of refresh we will just remove the rows from the grid. See how that goes.
                const ids = recordsToDelete.map((record) => record.id);
                this.optionRemove(ids, previewDelete, optionType);
            }), take(1), share());
        }
        formDesignerPropertiesGet(properties) {
            properties = super.formDesignerPropertiesGet(properties);
            properties.endpoint = ({
                id: 'endpoint',
                value: this._endpoint,
                componentType: 'Select',
                componentProperties: {
                    endpoint: 'Endpoint',
                    cacheRequest: true,
                    optionValueKey: 'endpoint',
                    optionDisplayKey: ['endpoint', 'idfield']
                },
                captionKey: 'formbuilder_endpoint',
                propertyGroup: 'data',
                propertyIndex: 3,
                saveFlag: () => !!this._endpoint
            });
            properties.action = ({
                id: 'action',
                value: this.action,
                componentType: 'Select',
                componentProperties: {
                    endpoint: 'Endpoint',
                    cacheRequest: true,
                    dynamicParameters: {
                        classname: 'endpoint'
                    },
                    staticParameters: {
                        method: 'all'
                    },
                    staticOptionData: [
                        { name: '', procedure: '', allowedrequestmethods: '' }
                    ],
                    optionValueKey: 'name',
                    optionDisplayKey: ['name', 'procedure' /*, 'allowedrequestmethods'*/]
                },
                captionKey: 'formbuilder_action',
                propertyGroup: 'data',
                propertyIndex: 3.1,
                saveFlag: () => !!this._action
            });
            properties.cacheRequest = ({
                id: 'cacheRequest',
                value: this.cacheRequest,
                componentType: 'InputCheckbox' // TODO: select once we have a way to request list of actions
                ,
                componentProperties: {
                    checkedValue: true,
                    unCheckedValue: false
                },
                captionKey: 'formbuilder_cacherequest',
                propertyGroup: 'data',
                propertyIndex: 3.2,
                saveFlag: () => this.cacheRequest
            });
            properties.dataRequestOnInit = ({
                id: 'dataRequestOnInit',
                value: this._dataRequestOnInit,
                componentType: 'InputCheckbox',
                componentProperties: {
                    checkedValue: true,
                    unCheckedValue: false,
                    labelWidth: 'auto'
                },
                captionKey: 'formbuilder_datarequestoninit',
                propertyGroup: 'data',
                propertyIndex: 4.3,
                saveFlag: () => !this._dataRequestOnInit
            });
            properties.dataRequestOnRecordLoad = ({
                id: 'dataRequestOnRecordLoad',
                value: this._dataRequestOnRecordLoad,
                componentType: 'InputCheckbox',
                componentProperties: {
                    checkedValue: true,
                    unCheckedValue: false,
                    labelWidth: 'auto'
                },
                captionKey: 'formbuilder_datarequestonload',
                propertyGroup: 'data',
                propertyIndex: 4.3,
                saveFlag: () => this._dataRequestOnRecordLoad
            });
            properties.dataRequestOnParameterChange = ({
                id: 'dataRequestOnParameterChange',
                value: this._dataRequestOnParameterChange,
                componentType: 'InputCheckbox',
                componentProperties: {
                    checkedValue: true,
                    unCheckedValue: false,
                    labelWidth: 'auto'
                },
                captionKey: 'formbuilder_datarequestonparameterchange',
                propertyGroup: 'data',
                propertyIndex: 4.4,
                saveFlag: () => !this._dataRequestOnParameterChange
            });
            properties.delayRequestUntilVisible = ({
                id: 'delayRequestUntilVisible',
                value: this._delayRequestUntilVisible,
                componentType: 'InputCheckbox',
                componentProperties: {
                    checkedValue: true,
                    unCheckedValue: false,
                    labelWidth: 'auto'
                },
                captionKey: 'formbuilder_delayrequestuntilvisible',
                propertyGroup: 'data',
                propertyIndex: 4.5,
                saveFlag: () => !this._delayRequestUntilVisible
            });
            properties.staticParameters = ({
                id: 'staticParameters',
                value: this._staticParameters || {},
                componentType: 'InputGrid',
                componentProperties: {
                    dataFormat: 'OBJECT',
                    dbGridName: 'ParameterMapping',
                    includeAddButton: true,
                    includeDeleteButton: true,
                    labelPosition: 'top',
                    grid: {
                        autoResize: false
                    }
                },
                captionKey: 'formbuilder_staticparameters',
                propertyGroup: 'data',
                propertyIndex: 4.6,
                saveFlag: () => !!this._staticParameters && Object.keys(this._staticParameters).length > 0
            });
            properties.dynamicParameters = ({
                id: 'dynamicParameters',
                value: this._dynamicParameters || {},
                componentType: 'InputGrid',
                componentProperties: {
                    dataFormat: 'OBJECT',
                    dbGridName: 'ParameterMapping',
                    includeAddButton: true,
                    includeDeleteButton: true,
                    labelPosition: 'top',
                    grid: {
                        autoResize: false
                    }
                },
                captionKey: 'formbuilder_dynamicparameters',
                propertyGroup: 'data',
                propertyIndex: 4.7,
                saveFlag: () => !!this._dynamicParameters && Object.keys(this._dynamicParameters).length > 0
            });
            properties.responseFilterConditions = ({
                id: 'responseFilterConditions',
                value: this._responseFilterConditions || [],
                componentType: 'InputGrid',
                componentProperties: {
                    dataFormat: 'ARRAY',
                    dbGridName: 'ChartConfigFilters',
                    includeAddButton: true,
                    includeDeleteButton: true,
                    labelPosition: 'top',
                    grid: {
                        autoResize: false
                    }
                },
                captionKey: 'formbuilder_responsefilterconditions',
                propertyGroup: 'data',
                propertyIndex: 4.8,
                saveFlag: () => !!this._responseFilterConditions && this._responseFilterConditions.length > 0
            });
            properties.offlineDataJoins = ({
                id: 'offlineDataJoins',
                value: this._offlineDataJoins || [],
                componentType: 'InputGrid',
                componentProperties: {
                    dataFormat: 'ARRAY',
                    dbGridName: 'OfflineDataJoins',
                    includeAddButton: true,
                    includeDeleteButton: true,
                    labelPosition: 'top',
                    hidden: this.endpoint.toLowerCase() !== 'offlinedata',
                    grid: {
                        autoResize: false
                    }
                },
                captionKey: 'formbuilder_offlinedatajoins',
                propertyGroup: 'data',
                propertyIndex: 4.85,
                saveFlag: () => !!this._offlineDataJoins && this._offlineDataJoins.length > 0
            });
            const options = [{ value: '', display: '' }];
            forEachKey(this.formContext?.componentsGet() || {}, (component) => {
                if (component.propertyGet('optionDataType') === this.optionDataType
                    && component.propertyGet('primaryDatasourceId') !== this.id // primary cant share with its replicas
                    && this.id !== component.id // cant share with self
                ) {
                    options.push({
                        value: component.id,
                        display: `${component.id} - ${component.propertyGet('endpoint')} - ${component.propertyGet('action')}`
                    });
                }
            });
            properties.primaryDatasourceId = ({
                id: 'primaryDatasourceId',
                value: this.primaryDatasourceId,
                componentType: 'Select',
                componentProperties: {
                    staticOptionData: options
                },
                captionKey: 'formbuilder_primarydatasourceid',
                propertyGroup: 'data',
                propertyIndex: 4.9,
                refreshPanelOnChange: true
                // ,handlePropertyChange: (newValue: any, oldValue: any, source: 'panel'|'component') => {
                // 	if (newValue) { // if we need any validations
                // 		const sharedComponent = this.formContext!.componentGet(newValue) as ListMixinInterface;
                // 	}
                // }
                ,
                saveFlag: !!this.primaryDatasourceId
            });
            properties.multiSelect = ({
                id: 'multiSelect',
                value: this.multiSelect,
                componentType: 'InputCheckbox',
                componentProperties: {
                    checkedValue: true,
                    unCheckedValue: false
                },
                captionKey: 'formbuilder_select_multiselect',
                propertyGroup: '',
                propertyIndex: 10,
                saveFlag: this.multiSelect
            });
            properties.responseResultName = ({
                id: 'responseResultName',
                value: this.responseResultName,
                componentType: 'Input',
                componentProperties: {},
                captionKey: 'formbuilder_responseresultname',
                propertyGroup: 'data',
                propertyIndex: 100,
                saveFlag: this.responseResultName !== 'result'
            });
            if (this.optionDataType === 'Record') {
                properties.baseTableName = {
                    id: 'baseTableName',
                    value: this.baseTableName,
                    componentType: 'Input',
                    componentProperties: {},
                    captionKey: 'formbuilder_record_basetablename',
                    propertyGroup: 'data',
                    saveFlag: !!this.baseTableName
                };
                properties.baseTablePkName = {
                    id: 'baseTablePkName',
                    value: this.baseTablePkName,
                    componentType: 'Input',
                    componentProperties: {},
                    captionKey: 'formbuilder_record_basetablepkname',
                    propertyGroup: 'data',
                    saveFlag: !!this.baseTablePkName
                };
            }
            if (this._resultSetMapping) {
                const map = {};
                forEachKey(this._resultSetMapping, (value, key) => {
                    map[key] = {
                        resultName: value.resultName,
                        baseTableName: value.baseTableName,
                        baseTablePkName: value.baseTablePkName
                    };
                });
                properties.resultSetMapping = ({
                    id: 'resultSetMapping',
                    value: map,
                    componentType: 'InputGrid',
                    componentProperties: {
                        dataFormat: 'OBJECTMAP',
                        dbGridName: 'ResultSetMapping',
                        includeAddButton: true,
                        includeDeleteButton: true,
                        labelPosition: 'top',
                        grid: {
                            autoResize: false
                        }
                    },
                    captionKey: 'formbuilder_resultsetmapping',
                    propertyGroup: 'data',
                    propertyIndex: 101,
                    saveFlag: () => !!this._resultSetMapping && Object.keys(this._resultSetMapping).length > 0
                });
            }
            if (this.primaryDatasourceId) {
                [
                    'endpoint',
                    'action',
                    'dynamicParameters',
                    'baseDynamicParameters',
                    'staticParameters',
                    'dataRequestOnRecordLoad',
                    'dataRequestOnParameterChange',
                    'dataRequestOnInit',
                    'delayRequestUntilVisible',
                    'cacheRequest',
                    'responseResultName',
                    'offlineDataJoins'
                ].forEach((prop) => {
                    if (properties[prop]) {
                        properties[prop].componentProperties.hidden = true;
                    }
                });
                if (Reflect.has(properties, 'parameterRecordName')) {
                    properties.parameterRecordName.componentProperties.hidden = true;
                }
            }
            return properties;
        }
        destroy() {
            if (this.primaryDatasourceComponent) {
                this._optionData = []; // let primary deal with destroying, it might not be getting destroyed yet (ex grid + chart scenario)
                this._deletedData = [];
            }
            super.destroy();
            this.primaryDatasourcePropertiesSubscription?.unsubscribe();
            this.primaryDatasourcePropertiesSubscription = undefined;
            this._primaryDatasourceComponent$ = undefined;
            this.clearOptionData();
            this.allOptionsById = {};
            this.optionData.length = 0;
            this.staticOptionData.length = 0;
            this.parameterRecord = undefined;
            this.cancelRequest$.next();
            this.cleanupOfflineDataJoinSubscriptions();
            this._changeSubject?.complete();
            this._changeSubject = undefined;
            this.changeSubjectSubscription = undefined;
        }
        renderMultiSelect() {
        }
        _removeActionMapping(properties) {
            delete properties.action;
            delete properties.endpoint;
            delete properties.optionValueKey;
            delete properties.optionDisplayKey;
            delete properties.cacheRequest;
        }
        _removeParamMapping(properties) {
            delete properties.staticParameters;
            delete properties.dynamicParameters;
            delete properties.dataRequestOnInit;
            delete properties.dataRequestOnParameterChange;
            delete properties.dataRequestOnRecordLoad;
            delete properties.delayRequestUntilVisible;
            delete properties.autoCompleteFlag;
        }
        parentRecordGet(_childRecord, _includeForm = false) {
            // implemented on GridTree / GanttChart
            return undefined;
        }
        childRecordsGet(_parentRecord, _recursive = false) {
            // implemented on GridTree / GanttChart
            return [];
        }
    };
}
